import useUiTranslation from "../utils/useUiTranslation";
import "../styles/RequestLists.css";
import AllRequestGuidance from "../components/requests/AllRequestGuidance";
import WorkspaceTableScroll from '../components/workspaces/WorkspaceTableScroll';
import WorkspaceSectionNav from '../components/workspaces/WorkspaceSectionNav';
import './OperationalWorkspaces.css';
// src/pages/AllRequestsPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import axios from "../api/axios";
import AssignRequestPanel from "../components/AssignRequestPanel";
import { printRequest, rewireRequestType } from "../api/requests";
import ApprovalTimeline from "../components/ApprovalTimeline";
import useApprovalTimeline from "../hooks/useApprovalTimeline";
import { getRequesterDisplay } from "../utils/requester";
import Card from "../components/Card";
import RequestAttachmentsSection from "../components/RequestAttachmentsSection";
import useRequestAttachments from "../hooks/useRequestAttachments";
import useCurrentUser from "../hooks/useCurrentUser";
import useStatusCommunications from "../hooks/useStatusCommunications";
import useDirectPurchaseCommunications from "../hooks/useDirectPurchaseCommunications";
import { hasPermission } from "../utils/permissions";
import RequestViewModeToggle from "../components/requests/RequestViewModeToggle";
import usePersistedRequestViewMode, {
  REQUEST_VIEW_MODES,
} from "../hooks/usePersistedRequestViewMode";
import PaginationControls from "../components/ui/PaginationControls";
import { formatOptionalItemText, getDisplayItems } from "../utils/itemUtils";
import { isCompletedRequestStatus } from "../utils/requestStatus";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Download,
  Filter,
  ListChecks,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Timer,
  X,
} from "lucide-react";

const PRINT_TRANSLATIONS = {
  en: {
    purchaseSummary: "Purchase Request Summary",
    generatedOn: "Generated on",
    requestDetails: "Request Details",
    justification: "Justification",
    requestedItems: "Requested Items",
    specs: "Specs:",
    approval: "Approval:",
    approvalSeparator: " – ",
    noItems: "No line items recorded.",
    tableHeaders: [
      "#",
      "Item",
      "Brand",
      "Qty",
      "Purchased Qty",
      "Unit Cost",
      "Total Cost",
    ],
    grandTotal: "Grand Total",
    preparedBy: "Prepared By",
    reviewedBy: "Reviewed By",
    approvedBy: "Approved By",
    requestId: "Request ID",
    status: "Status",
    requestType: "Request Type",
    createdOn: "Created On",
    neededBy: "Needed By",
    estimatedCost: "Estimated Cost",
    maintenanceRef: "Maintenance Ref #",
    project: "Project",
    department: "Department",
    section: "Section",
    requester: "Requester",
    printCount: "Print Count",
    finalApproval: "Final Approval",
    approvedAt: "Approved at",
    finalApprover: "Final approver",
    printLanguage: "Print Language",
    english: "English",
    arabic: "Arabic",
    yes: "Yes",
    no: "No",
    printConfirm: "Print this request? This will not increase its print count.",
  },
  ar: {
    purchaseSummary: "ملخص طلب الشراء",
    generatedOn: "تم الإنشاء في",
    requestDetails: "تفاصيل الطلب",
    justification: "المبررات",
    requestedItems: "المواد المطلوبة",
    specs: "المواصفات:",
    approval: "الاعتماد:",
    approvalSeparator: " – ",
    noItems: "لا توجد بنود مسجلة.",
    tableHeaders: [
      "#",
      "المادة",
      "العلامة التجارية",
      "الكمية",
      "الكمية المشتراة",
      "تكلفة الوحدة",
      "إجمالي التكلفة",
    ],
    grandTotal: "الإجمالي",
    preparedBy: "أعدها",
    reviewedBy: "تمت مراجعتها من",
    approvedBy: "تم اعتمادها من",
    requestId: "رقم الطلب",
    status: "الحالة",
    requestType: "نوع الطلب",
    createdOn: "تاريخ الإنشاء",
    neededBy: "مطلوب في",
    estimatedCost: "التكلفة التقديرية",
    maintenanceRef: "رقم مرجع الصيانة",
    project: "المشروع",
    department: "القسم",
    section: "الشعبة",
    requester: "مقدم الطلب",
    printCount: "عدد الطباعة",
    finalApproval: "الموافقة النهائية",
    approvedAt: "وافق بتاريخ",
    finalApprover: "المعتمد النهائي",
    printLanguage: "لغة الطباعة",
    english: "الإنجليزية",
    arabic: "العربية",
    yes: "نعم",
    no: "لا",
    printConfirm: "هل تريد طباعة هذا الطلب؟ لن يزيد هذا عدد الطباعة.",
  },
};
const DASHBOARD_REFRESH_EVENT = "dashboard:refresh";
const REQUEST_TYPE_FILTER_OPTIONS = [
  { value: "Stock", label: "Stock" },
  { value: "Non-Stock", label: "Non-Stock" },
  { value: "Medical Device", label: "Medical Device" },
  { value: "Medication", label: "Medication" },
  { value: "IT Item", label: "IT Item" },
  { value: "Maintenance", label: "Maintenance" },
  { value: "Printing Logbook", label: "Logbooks" },
  { value: "Warehouse Supply", label: "Warehouse Supply" },
];

// Map roles returned by the API to human friendly step labels
const STEP_LABELS = {
  HOD: "HOD Approval",
  CMO: "CMO Approval",
  SCM: "SCM Approval",
  COO: "COO Approval",
  CEO: "CEO Approval",
  CFO: "CFO Approval",
  WarehouseManager: "Warehouse Manager Approval",
  WarehouseKeeper: "Warehouse Keeper Approval",
  ProcurementSpecialist: "Procurement Specialist Action",
};

const getMaintenanceReference = (request) => {
  if (request?.request_type !== "Maintenance") return null;
  const reference = request?.maintenance_ref_number;
  return reference === null || reference === undefined || reference === ""
    ? "—"
    : reference;
};

const CURRENT_STEP_FILTER_OPTIONS = [
  { value: "Submitted", label: "Submitted" },
  { value: "Available in Stock", label: "Available in Stock" },
  { value: "Rejected", label: "Rejected" },
  { value: "Completed", label: "Completed" },
  {
    value: "Technical Inspection Pending",
    label: "Technical Inspection Pending",
  },
  { value: "Received", label: "Received" },
  { value: "Partially Procured", label: "Partially Procured" },
  { value: "Approved", label: "Approved" },
  ...Object.entries(STEP_LABELS).map(([value, label]) => ({ value, label })),
];

export const getCurrentStep = (req) => {
  const status = normalizeStatus(req.status);
  if (status === "rejected") return "Rejected";
  if (["cancelled", "canceled"].includes(status)) return "Cancelled";
  if (["on hold", "on_hold"].includes(status)) return "On Hold";
  if (status === "available in stock")
    return "Available in Stock";
  if (status === "completed") return "Completed";
  if (status === "technical_inspection_pending")
    return "Technical Inspection Pending";
  if (status === "received") return "Received";
  if (status === "partially procured")
    return "Partially Procured";
  if (status === "approved" && !req.current_approver_role)
    return "Approved";
  if (req.current_approver_role) {
    return (
      STEP_LABELS[req.current_approver_role] ||
      `${req.current_approver_role} Approval`
    );
  }
  return "Submitted";
};

// Map the current step to a colorful badge
export const getStepColor = (step) => {
  switch (step) {
    case "Cancelled":
    case "Rejected":
      return "bg-red-100 text-red-800";
    case "On Hold":
    case "Technical Inspection Pending":
      return "bg-amber-100 text-amber-800";
    case "Completed":
    case "Approved":
    case "Received":
    case "Available in Stock":
      return "bg-green-100 text-green-800";
    case "Partially Procured":
      return "bg-amber-100 text-amber-800";
    case "Submitted":
      return "bg-gray-100 text-gray-800";
    default:
      return "bg-blue-100 text-blue-800";
  }
};

export const getItemStatus = (item = {}) => {
  if (normalizeStatus(item.approval_status) === "rejected") {
    return { label: "Rejected", className: "bg-red-100 text-red-800" };
  }

  const status = normalizeStatus(item.procurement_status);
  if (status === "not_procured")
    return { label: "Not Procured", className: "bg-red-100 text-red-800" };
  if (["canceled", "cancelled"].includes(status))
    return { label: "Cancelled", className: "bg-red-100 text-red-800" };
  if (["purchased", "completed"].includes(status)) {
    return {
      label: status === "purchased" ? "Purchased" : "Completed",
      className: "bg-green-100 text-green-800",
    };
  }
  if (["partially_procured", "partially procured"].includes(status)) {
    return {
      label: "Partially Procured",
      className: "bg-amber-100 text-amber-800",
    };
  }
  if (!status || status === "pending")
    return { label: "Pending", className: "bg-amber-100 text-amber-800" };
  return {
    label: status
      .replace(/_/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase()),
    className: "bg-blue-100 text-blue-800",
  };
};

const normalizeStatus = (status) =>
  String(status || "")
    .trim()
    .toLowerCase();

const isPostApprovalStatus = (status) => {
  const normalized = normalizeStatus(status);
  return [
    "approved",
    "assigned",
    "partially procured",
    "technical_inspection_pending",
    "completed",
    "received",
  ].includes(normalized);
};

const AllRequestsPage = () => {
  const uiText = useUiTranslation();
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const [requestViewMode, setRequestViewMode] = usePersistedRequestViewMode(
    "all-requests-request-view-mode",
  );
  const [requests, setRequests] = useState([]);
  const [rewiringRequestId, setRewiringRequestId] = useState(null);
  const [requestTypeDrafts, setRequestTypeDrafts] = useState({});
  const [expandedAssignId, setExpandedAssignId] = useState(null);
  const [expandedItemsId, setExpandedItemsId] = useState(null);
  const [alphabetizedItemsId, setAlphabetizedItemsId] = useState(null);
  const [expandedAttachmentsId, setExpandedAttachmentsId] = useState(null);
  const [expandedCommunicationId, setExpandedCommunicationId] = useState(null);
  const [expandedDirectCommId, setExpandedDirectCommId] = useState(null);
  const [itemsMap, setItemsMap] = useState({});
  const [loadingItemsId, setLoadingItemsId] = useState(null);
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("");
  const [requestType, setRequestType] = useState("");
  const [requestDomain, setRequestDomain] = useState("");
  const [search, setSearch] = useState("");
  const [requestId, setRequestId] = useState("");
  const [maintenanceRefNumber, setMaintenanceRefNumber] = useState("");
  const [currentStep, setCurrentStep] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("");
  const [department, setDepartment] = useState("");
  const [section, setSection] = useState("");
  const [assignedUser, setAssignedUser] = useState("");
  const [centralSupplyStatus, setCentralSupplyStatus] = useState("");
  const [updatingCentralSupplyIds, setUpdatingCentralSupplyIds] = useState(
    () => new Set(),
  );
  const [departments, setDepartments] = useState([]);
  const [procurementUsers, setProcurementUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRequests, setTotalRequests] = useState(0);
  const [summaryCounts, setSummaryCounts] = useState({
    urgent: 0,
    approved: 0,
    pending: 0,
    completed: 0,
  });
  const [loadingExport, setLoadingExport] = useState(false);
  const [remindingApproverIds, setRemindingApproverIds] = useState(
    () => new Set(),
  );
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [printLanguage, setPrintLanguage] = useState("ar");
  const limit = 10;
  const getCurrentFilters = useCallback(
    () => ({
      filter,
      sort,
      requestType,
      requestDomain,
      search,
      requestId,
      maintenanceRefNumber,
      currentStep,
      fromDate,
      toDate,
      status,
      department,
      section,
      assignedUser,
      centralSupplyStatus,
    }),
    [
      assignedUser,
      centralSupplyStatus,
      currentStep,
      department,
      filter,
      fromDate,
      maintenanceRefNumber,
      requestId,
      requestType,
      requestDomain,
      search,
      section,
      sort,
      status,
      toDate,
    ],
  );
  const [appliedFilters, setAppliedFilters] = useState(() =>
    getCurrentFilters(),
  );
  const {
    expandedApprovalsId,
    approvalsMap,
    loadingApprovalsId,
    toggleApprovals,
    resetApprovals,
  } = useApprovalTimeline();
  const {
    attachmentsMap,
    attachmentLoadingMap,
    attachmentErrorMap,
    downloadingAttachmentId,
    loadAttachmentsForRequest,
    handleDownloadAttachment,
    resetAttachments,
  } = useRequestAttachments();
  const {
    canSendCommunication,
    canViewCommunication,
    communicationDrafts,
    communicationError,
    communicationList,
    communicationLoading,
    communicationSending,
    communicationSuccess,
    handleSendCommunication,
    refreshCommunications,
    setCommunicationDrafts,
  } = useStatusCommunications(user?.role);
  const {
    canDocumentDirectPurchase,
    drafts: directPurchaseDrafts,
    urgencyNotes: directPurchaseUrgency,
    sending: directPurchaseSending,
    error: directPurchaseError,
    success: directPurchaseSuccess,
    entries: directPurchaseEntries,
    setDrafts: setDirectPurchaseDrafts,
    setUrgencyNotes: setDirectPurchaseUrgency,
    handleSendDirectCommunication,
  } = useDirectPurchaseCommunications(user?.role);

  const sectionOptions = useMemo(() => {
    return departments.flatMap((dep) => {
      if (department && String(dep.id) !== String(department)) return [];
      return (dep.sections || []).map((sec) => ({
        ...sec,
        departmentName: dep.name,
      }));
    });
  }, [department, departments]);

  const requestSummary = useMemo(() => {
    return [
      {
        label: uiText("Total requests"),
        value: totalRequests,
        icon: ListChecks,
        tone: "blue",
      },
      {
        label: uiText("Urgent requests"),
        value: summaryCounts.urgent,
        icon: AlertTriangle,
        tone: "red",
      },
      {
        label: uiText("Approved"),
        value: summaryCounts.approved,
        icon: CheckCircle2,
        tone: "emerald",
      },
      {
        label: uiText("Pending"),
        value: summaryCounts.pending,
        icon: Timer,
        tone: "amber",
      },
      {
        label: uiText("Completed"),
        value: summaryCounts.completed,
        icon: CheckCircle2,
        tone: "violet",
      },
    ];
  }, [summaryCounts, totalRequests, uiText]);
  const activeFilterCount = useMemo(
    () =>
      Object.entries(appliedFilters).filter(
        ([key, value]) => key !== "sort" && Boolean(value),
      ).length,
    [appliedFilters],
  );
  const hasDraftFilterChanges = useMemo(
    () =>
      JSON.stringify(getCurrentFilters()) !== JSON.stringify(appliedFilters),
    [appliedFilters, getCurrentFilters],
  );
  const canHardDeleteRequests = hasPermission(user || {}, "requests.manage");
  const canUpdateCentralSupplyStatus = hasPermission(
    user || {},
    "requests.manage",
  );
  const canReclassifyRequests = hasPermission(
    user || {},
    "requests.reclassify",
  );
  const canRemindCurrentApprover =
    String(user?.role || "")
      .trim()
      .toUpperCase() === "SCM";
  const isSummaryRequestView = requestViewMode === REQUEST_VIEW_MODES.summary;

  useEffect(() => {
    const fetchDeps = async () => {
      try {
        const res = await axios.get("/departments");
        setDepartments(res.data);
      } catch (err) {
        console.error("❌ Failed to load departments:", err);
      }
    };
    fetchDeps();
  }, []);

  useEffect(() => {
    const fetchProcurementUsers = async () => {
      try {
        const res = await axios.get("/requests/procurement-users");
        setProcurementUsers(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("❌ Failed to load assigned-user filter options:", err);
      }
    };
    fetchProcurementUsers();
  }, []);

  const fetchRequests = useCallback(
    async (requestedPage = page) => {
      // This callback is also used directly as an event/success handler. React and
      // child components may pass an event or response object, neither of which is
      // a valid API page number.
      const targetPage =
        Number.isInteger(requestedPage) && requestedPage > 0
          ? requestedPage
          : page;
      setLoading(true);
      resetAttachments();
      try {
        const res = await axios.get("/requests", {
          params: {
            filter: appliedFilters.filter,
            sort: appliedFilters.sort,
            request_type: appliedFilters.requestType,
            request_domain: appliedFilters.requestDomain,
            search: appliedFilters.search,
            request_id: appliedFilters.requestId.trim() || undefined,
            maintenance_ref_number:
              appliedFilters.maintenanceRefNumber.trim() || undefined,
            current_step: appliedFilters.currentStep || undefined,
            from_date: appliedFilters.fromDate,
            to_date: appliedFilters.toDate,
            status: appliedFilters.status,
            department_id: appliedFilters.department,
            section_id: appliedFilters.section,
            assigned_to: appliedFilters.assignedUser,
            central_supply_status: appliedFilters.centralSupplyStatus,
            page: targetPage,
            limit,
          },
        });

        const fetchedRequests = Array.isArray(res?.data?.data)
          ? [...res.data.data]
          : [];
        const isCompletedOrRejected = (req) => {
          const normalizedStatus = normalizeStatus(req?.status);
          return (
            isCompletedRequestStatus(normalizedStatus) ||
            normalizedStatus === "rejected"
          );
        };

        const urgentPinnedRequests = [];
        const regularRequests = [];

        fetchedRequests.forEach((req) => {
          if (req?.is_urgent && !isCompletedOrRejected(req)) {
            urgentPinnedRequests.push(req);
          } else {
            regularRequests.push(req);
          }
        });

        setRequests([...urgentPinnedRequests, ...regularRequests]);
        // Item rows can be replaced when an edit approval is accepted. Do not keep
        // the pre-approval response cached after the request list is refreshed.
        setItemsMap({});
        setExpandedItemsId(null);
        setAlphabetizedItemsId(null);
        resetApprovals();
        const total = Number(res?.data?.total) || 0;
        setTotalRequests(total);
        setTotalPages(Math.ceil(total / limit));

        const summaryRes = await axios.get("/requests", {
          params: {
            filter: appliedFilters.filter,
            sort: appliedFilters.sort,
            request_type: appliedFilters.requestType,
            request_domain: appliedFilters.requestDomain,
            search: appliedFilters.search,
            request_id: appliedFilters.requestId.trim() || undefined,
            maintenance_ref_number:
              appliedFilters.maintenanceRefNumber.trim() || undefined,
            current_step: appliedFilters.currentStep || undefined,
            from_date: appliedFilters.fromDate,
            to_date: appliedFilters.toDate,
            status: appliedFilters.status,
            department_id: appliedFilters.department,
            section_id: appliedFilters.section,
            assigned_to: appliedFilters.assignedUser,
            central_supply_status: appliedFilters.centralSupplyStatus,
            page: 1,
            limit: Math.max(total, limit),
          },
        });

        const summaryRequests = Array.isArray(summaryRes?.data?.data)
          ? summaryRes.data.data
          : [];
        const finalizedStatuses = [
          "approved",
          "rejected",
          "completed",
          "received",
          "available in stock",
          "cancelled",
        ];
        const nextSummary = summaryRequests.reduce(
          (acc, req) => {
            const normalizedStatus = normalizeStatus(req?.status);
            if (req?.is_urgent) acc.urgent += 1;
            if (normalizedStatus === "approved") acc.approved += 1;
            if (isCompletedRequestStatus(normalizedStatus)) acc.completed += 1;
            if (!finalizedStatuses.includes(normalizedStatus)) acc.pending += 1;
            return acc;
          },
          { urgent: 0, approved: 0, pending: 0, completed: 0 },
        );
        setSummaryCounts(nextSummary);
      } catch (err) {
        console.error(err);
        alert("❌ Failed to fetch requests.");
      } finally {
        setLoading(false);
      }
    },
    [appliedFilters, page, resetApprovals, resetAttachments],
  );

  useEffect(() => {
    if (filtersChanged) {
      fetchRequests();
      setFiltersChanged(false);
    }
  }, [fetchRequests, filtersChanged]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters(getCurrentFilters());
    setFiltersChanged(false);
  };

  const clearFilters = () => {
    setFilter("");
    setSort("");
    setRequestType("");
    setRequestDomain("");
    setSearch("");
    setRequestId("");
    setMaintenanceRefNumber("");
    setCurrentStep("");
    setFromDate("");
    setToDate("");
    setStatus("");
    setDepartment("");
    setSection("");
    setAssignedUser("");
    setCentralSupplyStatus("");
    setPage(1);
    setAppliedFilters({
      filter: "",
      sort: "",
      requestType: "",
      requestDomain: "",
      search: "",
      requestId: "",
      maintenanceRefNumber: "",
      currentStep: "",
      fromDate: "",
      toDate: "",
      status: "",
      department: "",
      section: "",
      assignedUser: "",
      centralSupplyStatus: "",
    });
    setFiltersChanged(false);
  };

  const toggleItems = async (requestId) => {
    if (expandedItemsId === requestId) {
      setExpandedItemsId(null);
      setAlphabetizedItemsId(null);
      return;
    }
    setAlphabetizedItemsId(null);
    try {
      setLoadingItemsId(requestId);
      const res = await axios.get(`/requests/${requestId}/items`);
      setItemsMap((prev) => ({ ...prev, [requestId]: res.data.items || [] }));
    } catch (err) {
      console.error(`❌ Failed to load items for request ${requestId}:`, err);
      alert("Failed to load items");
    } finally {
      setLoadingItemsId(null);
    }
    setExpandedItemsId(requestId);
  };

  const toggleAttachments = async (requestId) => {
    if (expandedAttachmentsId === requestId) {
      setExpandedAttachmentsId(null);
      return;
    }

    await loadAttachmentsForRequest(requestId);
    setExpandedAttachmentsId(requestId);
  };

  const handleExport = async (type) => {
    setLoadingExport(true);
    try {
      const res = await axios.get(`/requests/export/${type}`, {
        params: {
          filter,
          sort,
          request_type: requestType,
          request_domain: requestDomain,
          search,
          request_id: requestId.trim() || undefined,
          maintenance_ref_number: maintenanceRefNumber.trim() || undefined,
          current_step: currentStep || undefined,
          from_date: fromDate,
          to_date: toDate,
          status,
          department_id: department,
          section_id: section,
          assigned_to: assignedUser,
        },
        responseType: "blob",
      });

      const blob = new Blob([res.data], {
        type: type === "csv" ? "text/csv" : "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const dateStr = new Date().toISOString().split("T")[0];
      link.download = `purchase_requests_${dateStr}.${type}`;

      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(`❌ Failed to export ${type.toUpperCase()}:`, err);
      alert(`❌ Failed to export ${type.toUpperCase()}`);
    } finally {
      setLoadingExport(false);
    }
  };

  const handlePrint = async (requestId) => {
    const translate = (key) =>
      PRINT_TRANSLATIONS[printLanguage]?.[key] ||
      PRINT_TRANSLATIONS.en[key] ||
      key;

    const shouldPrint = window.confirm(translate("printConfirm"));
    if (!shouldPrint) return;

    try {
      const data = await printRequest(requestId, {
        incrementPrintCount: false,
        language: printLanguage,
      });
      const {
        request,
        items,
        message = "Request ready for printing.",
        print_count,
      } = data;

      const locale = printLanguage === "ar" ? "ar-EG" : "en-US";
      const direction = printLanguage === "ar" ? "rtl" : "ltr";
      const win = window.open("", "_blank");
      if (!win) {
        alert("Please enable popups to print the request.");
        return;
      }

      const escapeHtml = (unsafe) => {
        if (unsafe === null || unsafe === undefined) return "";
        return String(unsafe)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#39;");
      };

      const formatValue = (value) => {
        if (value === null || value === undefined || value === "") return "—";
        if (typeof value === "boolean")
          return value ? translate("yes") : translate("no");
        return escapeHtml(value);
      };

      const formatDate = (value) => {
        if (!value) return "—";
        const date = new Date(value);
        return Number.isNaN(date.getTime())
          ? "—"
          : escapeHtml(date.toLocaleString(locale));
      };

      const formatAmount = (value) => {
        const numeric = Number(value);
        if (!Number.isFinite(numeric)) return formatValue(value);
        return escapeHtml(
          numeric.toLocaleString(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
        );
      };

      const formatFinalApprovalSummary = (name, dateValue) => {
        const formattedDate = formatDate(dateValue);
        if (formattedDate === "—") return formattedDate;
        return `${name || translate("finalApprover")} ${translate("approvedAt")} ${formattedDate}`;
      };

      const now = escapeHtml(new Date().toLocaleString(locale));
      const isMaintenanceRequest = request.request_type === "Maintenance";
      const maintenanceAssignedToRequester =
        isMaintenanceRequest &&
        request.initiated_by_technician_id &&
        request.requester_id &&
        request.requester_id !== request.initiated_by_technician_id;

      const maintenanceRequesterName = maintenanceAssignedToRequester
        ? request.requester_name
        : request.temporary_requester_name || request.requester_name;

      const requesterName = isMaintenanceRequest
        ? maintenanceRequesterName
        : request.requester_name || request.temporary_requester_name;

      const requesterRole = maintenanceAssignedToRequester
        ? request.requester_role
        : isMaintenanceRequest && request.temporary_requester_name
          ? null
          : request.requester_role;

      const requesterDisplay = requesterName
        ? `${requesterName}${requesterRole ? ` (${requesterRole})` : ""}`
        : requesterName;

      const finalApproval = request.final_approval?.approved_at
        ? formatFinalApprovalSummary(
            request.final_approval.approver_name,
            request.final_approval.approved_at,
          )
        : null;

      const detailFields = [
        { label: translate("requestId"), value: request.id },
        { label: translate("status"), value: request.status },
        {
          label: translate("createdOn"),
          value: formatDate(request.created_at),
        },
        { label: translate("neededBy"), value: formatDate(request.needed_by) },
        {
          label: translate("maintenanceRef"),
          value: request.maintenance_ref_number,
        },
        { label: translate("project"), value: request.project_name },
        { label: translate("department"), value: request.department_name },
        { label: translate("section"), value: request.section_name },
        { label: translate("requester"), value: requesterDisplay },
        { label: translate("printCount"), value: print_count },
        { label: translate("finalApproval"), value: finalApproval },
      ]
        .map(({ label, value }) => ({ label, value: formatValue(value) }))
        .filter(({ value }) => value && value !== "—");

      const detailGrid = detailFields
        .map(
          ({ label, value }) => `
            <div class="detail-item">
              <span class="detail-label">${escapeHtml(label)}</span>
              <span class="detail-value">${value}</span>
            </div>`,
        )
        .join("");

      const totalCost = items.reduce((sum, item) => {
        const value = Number(item.total_cost);
        return Number.isFinite(value) ? sum + value : sum;
      }, 0);

      const itemRows = items
        .map((item, index) => {
          const specs = formatOptionalItemText(item.specs, "");
          const specsNote = specs
            ? `<div class="item-note"><strong>${translate("specs")}</strong> ${formatValue(specs)}</div>`
            : "";
          const approvalNote =
            item.approval_status || item.approval_comments
              ? `<div class="item-note"><strong>${translate("approval")}</strong> ${formatValue(item.approval_status)}${
                  item.approval_comments
                    ? `${translate("approvalSeparator")}${formatValue(item.approval_comments)}`
                    : ""
                }</div>`
              : "";

          return `
            <tr>
              <td>${index + 1}</td>
              <td>
                <div class="item-name">${formatValue(item.item_name)}</div>
                ${specsNote || approvalNote ? `<div class="item-notes">${specsNote}${approvalNote}</div>` : ""}
              </td>
              <td>${formatValue(formatOptionalItemText(item.brand, ""))}</td>
              <td class="numeric">${formatValue(item.quantity)}</td>
              <td class="numeric">${formatValue(item.purchased_quantity)}</td>
              <td class="numeric">${formatAmount(item.unit_cost)}</td>
              <td class="numeric">${formatAmount(item.total_cost)}</td>
            </tr>`;
        })
        .join("");

      const justification = request.justification
        ? `<section class="section">
            <h2>${translate("justification")}</h2>
            <p>${escapeHtml(request.justification).replace(/\n/g, "<br />")}</p>
          </section>`
        : "";

      const body = `
        <!DOCTYPE html>
        <html lang="${printLanguage}" dir="${direction}">
          <head>
            <meta charset="UTF-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            <title>Request ${escapeHtml(request.id)}</title>
            <style>
              :root {
                color-scheme: light;
              }
              @page {
                size: A4;
                margin: 12mm;
              }
              body {
                font-family: 'Segoe UI', 'Cairo', Arial, sans-serif;
                color: #1f2937;
                margin: 0;
                padding: 32px;
                background: #f9fafb;
                direction: ${direction};
              }
              .page {
                background: #ffffff;
                border-radius: 12px;
                padding: 32px;
                box-shadow: 0 12px 24px rgba(15, 23, 42, 0.08);
              }
              header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                gap: 24px;
                border-bottom: 3px solid #2563eb;
                padding-bottom: 16px;
                margin-bottom: 24px;
                flex-direction: ${direction === "rtl" ? "row-reverse" : "row"};
              }
              header h1 {
                margin: 0;
                font-size: 28px;
                color: #111827;
              }
              header p {
                margin: 4px 0 0;
                color: #4b5563;
              }
              .print-badge {
                background: #2563eb;
                color: #ffffff;
                padding: 8px 16px;
                border-radius: 999px;
                font-weight: 600;
                font-size: 14px;
                align-self: center;
              }
              .details-grid {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
                gap: 16px;
                margin-bottom: 24px;
              }
              .detail-item {
                background: #f3f4f6;
                border-radius: 10px;
                padding: 12px 16px;
                border: 1px solid #e5e7eb;
              }
              .detail-label {
                display: block;
                font-size: 12px;
                letter-spacing: 0.06em;
                color: #6b7280;
                text-transform: uppercase;
                margin-bottom: 4px;
                text-align: ${direction === "rtl" ? "right" : "left"};
              }
              .detail-value {
                font-weight: 600;
                font-size: 15px;
                color: #111827;
                word-break: break-word;
                text-align: ${direction === "rtl" ? "right" : "left"};
              }
              .section {
                margin-bottom: 24px;
              }
              .section h2 {
                font-size: 18px;
                margin-bottom: 12px;
                color: #1d4ed8;
                border-bottom: 1px solid #c7d2fe;
                padding-bottom: 6px;
              }
              .items-table {
                width: 100%;
                border-collapse: collapse;
                font-size: 14px;
                background: #ffffff;
                overflow: hidden;
                border-radius: 12px;
                border: 1px solid #e5e7eb;
              }
              .items-table thead {
                background: linear-gradient(120deg, #1d4ed8, #2563eb);
                color: #ffffff;
              }
              .items-table th,
              .items-table td {
                padding: 12px;
                border-bottom: 1px solid #e5e7eb;
                vertical-align: top;
              }
              .items-table th {
                font-weight: 600;
                letter-spacing: 0.03em;
                text-transform: uppercase;
                font-size: 12px;
              }
              .items-table tbody tr:nth-child(even) {
                background: #f9fafb;
              }
              .item-name {
                font-weight: 600;
                color: #111827;
              }
              .item-notes {
                margin-top: 6px;
                color: #4b5563;
                font-size: 12px;
                display: flex;
                flex-direction: column;
                gap: 4px;
              }
              .item-note strong {
                color: #1f2937;
              }
              .numeric {
                text-align: right;
                white-space: nowrap;
              }
              .totals-row td {
                font-weight: 700;
                font-size: 15px;
                color: #111827;
                background: #eef2ff;
              }
              .signature-blocks {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                gap: 24px;
                margin-top: 32px;
              }
              .signature {
                border-top: 1px solid #9ca3af;
                padding-top: 12px;
                text-align: center;
                font-size: 12px;
                color: #6b7280;
              }
              footer {
                margin-top: 32px;
                font-size: 12px;
                color: #6b7280;
                text-align: right;
              }
              @media print {
                body {
                  padding: 0;
                  background: #ffffff;
                }
                .page {
                  box-shadow: none;
                  border-radius: 0;
                  padding: 0;
                }
                header {
                  margin-bottom: 16px;
                }
                .section {
                  margin-bottom: 12px;
                }
                .section h2 {
                  margin-top: 0;
                  margin-bottom: 6px;
                  padding-bottom: 3px;
                }
                .details-grid {
                  gap: 8px;
                  margin-bottom: 12px;
                }
                .detail-item {
                  background: transparent;
                  padding: 6px 10px;
                }
                .items-table {
                  border: 1px solid #d1d5db;
                }
                .items-table tbody tr:nth-child(even) {
                  background: #ffffff;
                }
                .items-table th,
                .items-table td {
                  padding: 6px;
                }
                .signature-blocks {
                  margin-top: 20px;
                }
                footer {
                  margin-top: 16px;
                }
              }
            </style>
          </head>
          <body>
            <div class="page">
              <header>
                <div>
                  <h1>${translate("purchaseSummary")}</h1>
                  <p>${translate("generatedOn")} ${now}</p>
                </div>
                <span class="print-badge">${translate("printCount")}: ${formatValue(print_count)}</span>
              </header>

              <section class="section">
                <h2>${translate("requestDetails")}</h2>
                <div class="details-grid">
                  ${detailGrid}
                </div>
              </section>

              ${justification}

              <section class="section">
                <h2>${translate("requestedItems")}</h2>
                <table class="items-table">
                  <thead>
                    <tr>
                      ${PRINT_TRANSLATIONS[printLanguage].tableHeaders
                        .map((header) => `<th>${escapeHtml(header)}</th>`)
                        .join("")}
                    </tr>
                  </thead>
                  <tbody>
                    ${
                      itemRows ||
                      `<tr><td colspan="7" style="text-align:center; padding: 24px;">${translate("noItems")}</td></tr>`
                    }
                    <tr class="totals-row">
                      <td colspan="6">${translate("grandTotal")}</td>
                      <td class="numeric">${formatAmount(totalCost)}</td>
                    </tr>
                  </tbody>
                </table>
              </section>

              <section class="signature-blocks">
                <div class="signature">${translate("preparedBy")}</div>
                <div class="signature">${translate("reviewedBy")}</div>
                <div class="signature">${translate("approvedBy")}</div>
              </section>

              <footer>
                ${translate("requestId")} ${escapeHtml(request.id)} • ${now}
              </footer>
            </div>
          </body>
        </html>
      `;

      win.document.write(body);
      win.document.close();
      win.onload = () => {
        win.focus();
        win.print();
      };

      alert(message);
    } catch (err) {
      console.error("❌ Failed to print request:", err);
      alert("❌ Failed to print request.");
    }
  };

  const handleRemindCurrentApprover = async (requestId) => {
    const confirmed = window.confirm(
      `Send an email reminder to the current approver for request ${requestId}?`,
    );
    if (!confirmed) return;

    setRemindingApproverIds((prev) => new Set(prev).add(requestId));
    try {
      const res = await axios.post(
        `/approvals/request/${requestId}/remind-current`,
      );
      alert(res?.data?.message || "✅ Approval reminder email sent.");
    } catch (err) {
      console.error(
        `❌ Failed to remind current approver for request ${requestId}:`,
        err,
      );
      alert(
        err?.response?.data?.message || "❌ Failed to send approval reminder.",
      );
    } finally {
      setRemindingApproverIds((prev) => {
        const next = new Set(prev);
        next.delete(requestId);
        return next;
      });
    }
  };

  const handleHardDelete = async (requestId) => {
    const confirmed = window.confirm(
      `Delete request ${requestId} permanently? This removes the request and all related records.`,
    );
    if (!confirmed) return;

    const password = window.prompt(
      "Enter your password to confirm this deletion:",
    );
    if (password === null) return;
    if (!password.trim()) {
      alert("Password is required to delete a request.");
      return;
    }

    try {
      await axios.post("/auth/verify-password", { password });
      await axios.delete(`/requests/${requestId}/hard-delete`);
      if (expandedAssignId === requestId) setExpandedAssignId(null);
      if (expandedItemsId === requestId) setExpandedItemsId(null);
      if (expandedAttachmentsId === requestId) setExpandedAttachmentsId(null);
      if (expandedCommunicationId === requestId)
        setExpandedCommunicationId(null);
      if (expandedDirectCommId === requestId) setExpandedDirectCommId(null);
      alert("✅ Request deleted permanently.");
      fetchRequests();
      window.dispatchEvent(new Event(DASHBOARD_REFRESH_EVENT));
    } catch (err) {
      console.error(`❌ Failed to delete request ${requestId}:`, err);
      alert(err?.response?.data?.message || "Failed to delete request.");
    }
  };

  const handleRequestTypeChange = async (request) => {
    const nextType = requestTypeDrafts[request.id] || request.request_type;
    if (nextType === request.request_type) return;
    const confirmed = window.confirm(
      `Change request ${request.id} from ${request.request_type} to ${nextType}? ` +
        "Its existing approvals will be removed and the correct approval workflow will restart from the first step.",
    );
    if (!confirmed) return;

    setRewiringRequestId(request.id);
    try {
      const result = await rewireRequestType(request.id, nextType);
      alert(`✅ ${result.message}`);
      setRequestTypeDrafts((previous) => {
        const next = { ...previous };
        delete next[request.id];
        return next;
      });
      await fetchRequests();
      window.dispatchEvent(new Event(DASHBOARD_REFRESH_EVENT));
    } catch (err) {
      console.error(
        `❌ Failed to change the type of request ${request.id}:`,
        err,
      );
      alert(
        err?.response?.data?.message || "Failed to change the request type.",
      );
    } finally {
      setRewiringRequestId(null);
    }
  };

  const handleCentralSupplyStatus = async (request) => {
    const sent = !request.sent_to_central_supply_at;
    setUpdatingCentralSupplyIds((current) => new Set(current).add(request.id));
    try {
      const response = await axios.patch(
        `/requests/${request.id}/central-supply-status`,
        { sent },
      );
      setRequests((current) =>
        current.map((entry) =>
          entry.id === request.id ? { ...entry, ...response.data } : entry,
        ),
      );
    } catch (err) {
      console.error("Failed to update Central Supply Chain status:", err);
      alert(
        err?.response?.data?.message ||
          "Failed to update Central Supply Chain status.",
      );
    } finally {
      setUpdatingCentralSupplyIds((current) => {
        const next = new Set(current);
        next.delete(request.id);
        return next;
      });
    }
  };

  const fieldClass =
    "h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100";
  const filterLabelClass =
    "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500";
  const summaryToneClasses = {
    blue: "bg-blue-50 text-blue-700 ring-blue-100",
    red: "bg-red-50 text-red-700 ring-red-100",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
    violet: "bg-violet-50 text-violet-700 ring-violet-100",
  };

  return (
    <div className="min-h-screen bg-slate-50/70 px-4 py-6 sm:px-6 lg:px-8">
      <div className="operational-workspace operational-request-list procurement-workspace mx-auto max-w-7xl">
        <section className="relative mb-6 overflow-hidden rounded-2xl procurement-workspace-header bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 px-6 py-7 text-white shadow-xl shadow-blue-950/10 sm:px-8">
          <div
            className="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-blue-400/20 blur-3xl"
            aria-hidden="true"
          />
          <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-blue-200">
                <ListChecks size={15} aria-hidden="true" />{" "}{uiText("Procurement workspace")}</div>
              <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{uiText("All purchase requests")}</h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-blue-100 sm:text-base">{uiText("Review demand, follow approvals, and move every request from submission to completion.")}</p>
            </div>
            <button
              type="button"
              className="inline-flex h-11 items-center justify-center gap-2 self-start rounded-xl border border-white/20 bg-white/10 px-4 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={() => fetchRequests()}
              disabled={loading}
            >
              <RefreshCw
                size={17}
                className={loading ? "animate-spin" : ""}
                aria-hidden="true"
              />
              {loading ? uiText("Refreshing…") : uiText("Refresh data")}
            </button>
          </div>
        </section>

        <section
          className="workspace-kpi-grid mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5"
          aria-label={uiText("Request overview")}
        >
          {requestSummary.map((item) => {
            const SummaryIcon = item.icon;
            return (
              <Card
                key={uiText(item.label)}
                className="group flex min-h-[112px] items-center justify-between overflow-hidden transition hover:-translate-y-0.5 hover:shadow-md"
              >
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {uiText(item.label)}
                  </p>
                  <p className="mt-2 text-3xl font-bold tracking-tight text-slate-900">
                    {item.value.toLocaleString()}
                  </p>
                </div>
                <span
                  className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ring-1 ${summaryToneClasses[item.tone]}`}
                >
                  <SummaryIcon size={21} aria-hidden="true" />
                </span>
              </Card>
            );
          })}
        </section>

        <WorkspaceSectionNav sections={[{ id: 'all-filters', label: 'filters' }, { id: 'all-results', label: 'requestResults' }]} />
        <div id="all-filters" tabIndex={-1}>
        <Card className="workspace-filter-panel mb-5 overflow-hidden !p-0">
          <div className="border-b border-slate-200 bg-white px-5 py-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-3">
                <span className="grid h-9 w-9 place-items-center rounded-lg bg-blue-50 text-blue-700">
                  <Filter size={18} aria-hidden="true" />
                </span>
                <div>
                  <h2 className="font-semibold text-slate-900">{uiText("Find requests")}</h2>
                  <p className="text-xs text-slate-500">{uiText("Showing {{shown}} of {{total}} requests", { shown: requests.length.toLocaleString(), total: totalRequests.toLocaleString() })}</p>
                </div>
                {activeFilterCount > 0 && (
                  <span className="rounded-full bg-blue-100 px-2.5 py-1 text-xs font-bold text-blue-700">
                    {activeFilterCount}{" "}{uiText("active")}</span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {hasDraftFilterChanges && (
                  <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">{uiText("Changes not applied")}</span>
                )}
                <button
                  type="button"
                  onClick={() => setShowAdvancedFilters((value) => !value)}
                  className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  aria-expanded={showAdvancedFilters}
                >
                  <SlidersHorizontal size={16} aria-hidden="true" />{" "}{uiText("More filters")}{" "}
                  <ChevronDown
                    size={15}
                    className={`transition ${showAdvancedFilters ? "rotate-180" : ""}`}
                    aria-hidden="true"
                  />
                </button>
                <button
                  type="button"
                  onClick={clearFilters}
                  disabled={!activeFilterCount && !hasDraftFilterChanges}
                  className="inline-flex h-10 items-center gap-1.5 rounded-lg px-3 text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                >
                  <X size={16} aria-hidden="true" />{" "}{uiText("Clear")}</button>
              </div>
            </div>
          </div>
          <form
            className="bg-white p-5"
            onSubmit={(event) => {
              event.preventDefault();
              applyFilters();
            }}
          >
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
              <label className="sm:col-span-2 lg:col-span-2">
                <span className={filterLabelClass}>{uiText("Search")}</span>
                <span className="relative block">
                  <Search
                    size={17}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    aria-hidden="true"
                  />
                  <input
                    type="search"
                    className={`${fieldClass} pl-9`}
                    placeholder={uiText("ID, requester, project, or keyword")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </span>
              </label>
              <label>
                <span className={filterLabelClass}>{uiText("Status")}</span>
                <select
                  className={fieldClass}
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">{uiText("All statuses")}</option>
                  <option value="Pending">{uiText("Pending")}</option>
                  <option value="Approved">{uiText("Approved")}</option>
                  <option value="Partially Procured">{uiText("Partially Procured")}</option>
                  <option value="Received">{uiText("Received")}</option>
                  <option value="Rejected">{uiText("Rejected")}</option>
                  <option value="Completed">{uiText("Completed")}</option>
                </select>
              </label>
              <label>
                <span className={filterLabelClass}>{uiText("Request type")}</span>
                <select
                  className={fieldClass}
                  value={requestType}
                  onChange={(e) => setRequestType(e.target.value)}
                >
                  <option value="">{uiText("All types")}</option>
                  {REQUEST_TYPE_FILTER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {uiText(option.label)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span className={filterLabelClass}>{uiText("Department")}</span>
                <select
                  className={fieldClass}
                  value={department}
                  onChange={(e) => {
                    setDepartment(e.target.value);
                    setSection("");
                  }}
                >
                  <option value="">{uiText("All departments")}</option>
                  {departments.map((dep) => (
                    <option key={dep.id} value={dep.id}>
                      {dep.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span className={filterLabelClass}>{uiText("Current step")}</span>
                <select
                  className={fieldClass}
                  value={currentStep}
                  onChange={(e) => setCurrentStep(e.target.value)}
                >
                  <option value="">{uiText("All current steps")}</option>
                  {CURRENT_STEP_FILTER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {uiText(option.label)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {showAdvancedFilters && (
              <div className="mt-5 grid gap-3 border-t border-slate-100 pt-5 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
                <label>
                  <span className={filterLabelClass}>{uiText("Assignment")}</span>
                  <select
                    className={fieldClass}
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                  >
                    <option value="">{uiText("All requests")}</option>
                    <option value="unassigned">{uiText("Unassigned only")}</option>
                  </select>
                </label>
                <label>
                  <span className={filterLabelClass}>{uiText("Sort order")}</span>
                  <select
                    className={fieldClass}
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                  >
                    <option value="">{uiText("Newest first")}</option>
                    <option value="assigned">{uiText("Assigned first")}</option>
                  </select>
                </label>
                <label>
                  <span className={filterLabelClass}>{uiText("Domain")}</span>
                  <select
                    className={fieldClass}
                    value={requestDomain}
                    onChange={(e) => setRequestDomain(e.target.value)}
                  >
                    <option value="">{uiText("All domains")}</option>
                    <option value="operational">{uiText("Operational")}</option>
                    <option value="medical">{uiText("Medical")}</option>
                  </select>
                </label>
                <label>
                  <span className={filterLabelClass}>{uiText("Request ID")}</span>
                  <input
                    className={fieldClass}
                    placeholder={uiText("Exact ID")}
                    value={requestId}
                    onChange={(e) => setRequestId(e.target.value)}
                  />
                </label>
                <label>
                  <span className={filterLabelClass}>{uiText("Maintenance ref.")}</span>
                  <input
                    className={fieldClass}
                    placeholder={uiText("Reference number")}
                    value={maintenanceRefNumber}
                    onChange={(e) => setMaintenanceRefNumber(e.target.value)}
                  />
                </label>
                <label>
                  <span className={filterLabelClass}>{uiText("Section")}</span>
                  <select
                    className={fieldClass}
                    value={section}
                    onChange={(e) => setSection(e.target.value)}
                  >
                    <option value="">{uiText("All sections")}</option>
                    {sectionOptions.map((sec) => (
                      <option key={sec.id} value={sec.id}>
                        {department
                          ? sec.name
                          : `${sec.departmentName} — ${sec.name}`}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span className={filterLabelClass}>{uiText("Assigned user")}</span>
                  <select
                    className={fieldClass}
                    value={assignedUser}
                    onChange={(e) => setAssignedUser(e.target.value)}
                  >
                    <option value="">{uiText("All assigned users")}</option>
                    <option value="unassigned">{uiText("Unassigned")}</option>
                    {procurementUsers.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span className={filterLabelClass}>{uiText("Central Supply")}</span>
                  <select
                    className={fieldClass}
                    value={centralSupplyStatus}
                    onChange={(e) => setCentralSupplyStatus(e.target.value)}
                  >
                    <option value="">{uiText("Any transfer status")}</option>
                    <option value="not_sent">{uiText("Not sent")}</option>
                    <option value="sent">{uiText("Sent")}</option>
                  </select>
                </label>
                <label>
                  <span className={filterLabelClass}>{uiText("From date")}</span>
                  <input
                    type="date"
                    className={fieldClass}
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </label>
                <label>
                  <span className={filterLabelClass}>{uiText("To date")}</span>
                  <input
                    type="date"
                    className={fieldClass}
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </label>
                <label>
                  <span className={filterLabelClass}>{uiText("Print language")}</span>
                  <select
                    id="print-language"
                    className={fieldClass}
                    value={printLanguage}
                    onChange={(e) => setPrintLanguage(e.target.value)}
                  >
                    <option value="en">{uiText("English")}</option>
                    <option value="ar">{uiText("Arabic")}</option>
                  </select>
                </label>
              </div>
            )}
            <div className="mt-5 flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-slate-500">{uiText("Press Enter to apply your filters.")}</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleExport("csv")}
                  className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  disabled={loadingExport}
                >
                  <Download size={16} aria-hidden="true" /> CSV
                </button>
                <button
                  type="button"
                  onClick={() => handleExport("pdf")}
                  className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  disabled={loadingExport}
                >
                  <Download size={16} aria-hidden="true" /> PDF
                </button>
                <button
                  type="submit"
                  className="inline-flex h-10 items-center gap-2 rounded-lg bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
                >
                  <Search size={16} aria-hidden="true" />{" "}{uiText("Apply filters")}</button>
              </div>
            </div>
          </form>
        </Card>

        </div>

        {requests.length > 0 && (
          <PaginationControls
            ariaLabel="Pagination (top)"
            disabled={loading}
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            summary={`Page ${page} of ${totalPages}`}
          />
        )}

        <RequestViewModeToggle
          className="mb-4"
          value={requestViewMode}
          onChange={setRequestViewMode}
          description="Use summary view to scan IT, stock, non-stock, maintenance, and other request types without opening every detailed card."
        />

        <div id="all-results" tabIndex={-1} className="workspace-results-anchor" />
        {loading ? (
          <p className="text-gray-600">{uiText("Loading requests...")}</p>
        ) : requests.length === 0 ? (
          <Card>
            <p className="text-sm text-gray-600">{uiText("No requests found for the selected filters. Try adjusting or clearing filters.")}</p>
            <button
              type="button"
              onClick={clearFilters}
              className="mt-3 rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              {t("allRequestGuidance.reset")}
            </button>
          </Card>
        ) : (
          <div className="space-y-4">
            {requests.map((request) => {
              const step = getCurrentStep(request);
              const isUrgent = Boolean(request?.is_urgent);
              const cardClasses = isUrgent
                ? "request-card--urgent"
                : "";
              const requesterDisplay = getRequesterDisplay(request);
              const showCommunication =
                canViewCommunication && isPostApprovalStatus(request.status);
              const isCommunicationExpanded =
                expandedCommunicationId === request.id;
              const showDirectPurchaseSection =
                canDocumentDirectPurchase && isUrgent;
              const isDirectPurchaseExpanded =
                expandedDirectCommId === request.id;
              const statusLabel = request.status || step;
              const loadedItems = itemsMap[request.id] || [];
              const itemCount = Number(
                request.item_count ??
                  request.items_count ??
                  loadedItems.length ??
                  0,
              );
              const attachmentCount = Number(
                request.attachment_count ??
                  request.attachments_count ??
                  (attachmentsMap[request.id] || []).length,
              );
              const estimatedCostValue = Number(request.estimated_cost || 0);
              const assignedDisplay = request.assigned_user_name
                ? `${request.assigned_user_name}${request.assigned_user_role ? ` (${request.assigned_user_role})` : ""}`
                : request.split_assignees?.length > 0
                  ? `Split among ${request.split_assignees.map((user) => user.name).join(", ")}`
                  : uiText("Not Assigned");

              const toggleCommunication = (requestId) => {
                const nextId =
                  expandedCommunicationId === requestId ? null : requestId;
                setExpandedCommunicationId(nextId);

                if (
                  nextId &&
                  !communicationList[requestId] &&
                  !communicationLoading[requestId]
                ) {
                  refreshCommunications(requestId);
                }
              };

              return (
                <Card key={request.id} className={`request-list-card ${isSummaryRequestView ? "request-list-card--summary" : "request-list-card--detailed"} transition ${cardClasses}`}>
                  <div className="request-card-layout">
                    <div
                      className={
                        isSummaryRequestView
                          ? "request-card-metadata request-card-metadata--summary"
                          : "request-card-metadata request-card-metadata--detailed"
                      }
                    >
                      <div className="flex items-center gap-3 flex-wrap">
                        <p className="font-semibold text-gray-800">{uiText("ID:")}{" "}{request.id}
                        </p>
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${request.sent_to_central_supply_at ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}
                          title={
                            request.sent_to_central_supply_at
                              ? `Sent ${new Date(request.sent_to_central_supply_at).toLocaleString()}`
                              : uiText("This request has not been sent to the Central Supply Chain Center")
                          }
                        >
                          <span
                            className={`h-2 w-2 rounded-full ${request.sent_to_central_supply_at ? "bg-emerald-500" : "bg-amber-500"}`}
                            aria-hidden="true"
                          />
                          {request.sent_to_central_supply_at
                            ? uiText("Sent to Central Supply")
                            : uiText("Not sent to Central Supply")}
                        </span>
                        {isUrgent && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide">
                            <span
                              className="block h-2 w-2 rounded-full bg-red-500"
                              aria-hidden="true"
                            />{uiText("Urgent")}</span>
                        )}
                        {isSummaryRequestView && (
                          <>
                            <span
                              className={`px-2 py-1 rounded text-xs font-semibold ${getStepColor(step)}`}
                            >
                              {uiText(step)}
                            </span>
                            <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-semibold text-blue-700">
                              {uiText(request.request_type || uiText("Request"))}
                            </span>
                          </>
                        )}
                      </div>

                      {isSummaryRequestView ? (
                        <>
                          <p className="line-clamp-2 text-sm font-medium text-gray-800">
                            {request.justification ||
                              uiText("No justification provided.")}
                          </p>
                          <div
                            className="flex flex-wrap gap-2 text-xs font-medium text-slate-600"
                            aria-label={`Summary for request ${request.id}`}
                          >
                            {getMaintenanceReference(request) && (
                              <span className="rounded-full bg-slate-100 px-2 py-0.5">{uiText("Maintenance Ref #:")}{" "}
                                {getMaintenanceReference(request)}
                              </span>
                            )}
                            <span className="rounded-full bg-slate-100 px-2 py-0.5">{uiText("Project:")}{" "}{request.project_name || "—"}
                            </span>
                            <span className="rounded-full bg-slate-100 px-2 py-0.5">{uiText("Department:")}{" "}{request.department_name || "—"}
                            </span>
                            <span className="rounded-full bg-slate-100 px-2 py-0.5">{uiText("Requester:")}{" "}{requesterDisplay}
                            </span>
                            <span className="rounded-full bg-slate-100 px-2 py-0.5">{uiText("Assigned:")}{" "}{assignedDisplay}
                            </span>
                            <span className="rounded-full bg-slate-100 px-2 py-0.5">{uiText("Items:")}{" "}
                              {Number.isFinite(itemCount) ? itemCount : 0}
                            </span>
                            <span className="rounded-full bg-slate-100 px-2 py-0.5">{uiText("Attachments:")}{" "}{attachmentCount}
                            </span>
                            <span className="rounded-full bg-slate-100 px-2 py-0.5">{uiText("Estimated:")}{" "}{estimatedCostValue.toLocaleString()}{" "}
                              IQD
                            </span>
                          </div>
                        </>
                      ) : (
                        <>
                          <p>
                            <strong>{uiText("Type:")}</strong> {request.request_type}
                          </p>
                          {getMaintenanceReference(request) && (
                            <p>
                              <strong>{uiText("Maintenance Ref #:")}</strong>{" "}
                              {getMaintenanceReference(request)}
                            </p>
                          )}
                          <p>
                            <strong>{uiText("Project:")}</strong>{" "}
                            {request.project_name || "—"}
                          </p>
                          <p>
                            <strong>{uiText("Department:")}</strong>{" "}
                            {request.department_name || "—"}
                          </p>
                          <p>
                            <strong>{uiText("Section:")}</strong>{" "}
                            {request.section_name || "—"}
                          </p>
                          <p>
                            <strong>{uiText("Requester:")}</strong> {requesterDisplay}
                          </p>
                          <p>
                            <strong>{uiText("Assigned To:")}</strong> {assignedDisplay}
                          </p>
                          <p className="request-card-justification">
                            <strong>{uiText("Justification:")}</strong>{" "}
                            {request.justification}
                          </p>
                        </>
                      )}
                    </div>

                    <div className="request-card-actions">
                      {canUpdateCentralSupplyStatus && (
                        <button
                          type="button"
                          className={`rounded px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-60 ${request.sent_to_central_supply_at ? "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}
                          onClick={() => handleCentralSupplyStatus(request)}
                          disabled={updatingCentralSupplyIds.has(request.id)}
                        >
                          {updatingCentralSupplyIds.has(request.id)
                            ? uiText("Updating...")
                            : request.sent_to_central_supply_at
                              ? uiText("Mark as not sent")
                              : uiText("Mark sent to Central Supply")}
                        </button>
                      )}
                      <Link
                        to={`/requests/${request.id}`}
                        className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                      >{uiText("Open Workspace")}</Link>
                      <button
                        className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
                        onClick={() => handlePrint(request.id)}
                      >{uiText("Print")}</button>
                      {canRemindCurrentApprover && (
                        <button
                          type="button"
                          className="rounded bg-amber-600 px-4 py-2 text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                          onClick={() =>
                            handleRemindCurrentApprover(request.id)
                          }
                          disabled={
                            remindingApproverIds.has(request.id) ||
                            !request.current_approver_role
                          }
                          title={
                            request.current_approver_role
                              ? uiText("Email a reminder to the current approver")
                              : uiText("No current approver to remind")
                          }
                        >
                          {remindingApproverIds.has(request.id)
                            ? uiText("Sending reminder...")
                            : uiText("Remind Approver")}
                        </button>
                      )}
                      {isPostApprovalStatus(request.status) && (
                        <Link
                          to={`/requests/${request.id}/procure-to-pay/purchase-orders`}
                          className="rounded bg-amber-600 px-4 py-2 text-white hover:bg-amber-700"
                          title={uiText("Create a governed purchase order from approved supplier awards")}
                        >{uiText("Create Purchase Order")}</Link>
                      )}
                      {canHardDeleteRequests && (
                        <button
                          className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                          onClick={() => handleHardDelete(request.id)}
                        >{uiText("Delete Permanently")}</button>
                      )}
                      {canReclassifyRequests && (
                        <details className="request-type-correction"><summary>{uiText("Correct request type")}</summary><div className="request-type-correction-fields">
                          <label
                            className="mb-1 block text-xs font-semibold text-blue-900"
                            htmlFor={`request-type-${request.id}`}
                          >{uiText("Correct request type")}</label>
                          <select
                            id={`request-type-${request.id}`}
                            className="w-full rounded border border-blue-300 bg-white px-2 py-2 text-sm text-gray-900"
                            value={
                              requestTypeDrafts[request.id] ??
                              request.request_type
                            }
                            onChange={(event) =>
                              setRequestTypeDrafts((previous) => ({
                                ...previous,
                                [request.id]: event.target.value,
                              }))
                            }
                            disabled={rewiringRequestId === request.id}
                          >
                            {REQUEST_TYPE_FILTER_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {uiText(option.label)}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="mt-2 w-full rounded bg-blue-700 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={() => handleRequestTypeChange(request)}
                            disabled={
                              rewiringRequestId === request.id ||
                              (requestTypeDrafts[request.id] ??
                                request.request_type) === request.request_type
                            }
                          >
                            {rewiringRequestId === request.id
                              ? uiText("Rewiring approvals…")
                              : uiText("Change & rewire")}
                          </button>
                        </div></details>
                      )}
                      <button
                        className="text-blue-600 underline"
                        onClick={() => toggleItems(request.id)}
                        disabled={loadingItemsId === request.id}
                      >
                        {expandedItemsId === request.id
                          ? uiText("Hide Items")
                          : uiText("View Items")}
                      </button>
                      <button
                        className="text-blue-600 underline"
                        onClick={() => toggleAttachments(request.id)}
                        disabled={attachmentLoadingMap[request.id]}
                      >
                        {expandedAttachmentsId === request.id
                          ? uiText("Hide Attachments")
                          : uiText("View Attachments")}
                      </button>
                      <button
                        className="text-blue-600 underline"
                        onClick={() => toggleApprovals(request.id)}
                        disabled={loadingApprovalsId === request.id}
                      >
                        {expandedApprovalsId === request.id
                          ? uiText("Hide Approvals")
                          : uiText("View Approvals")}
                      </button>
                      {showCommunication && (
                        <button
                          className="text-indigo-700 underline"
                          onClick={() => toggleCommunication(request.id)}
                          disabled={communicationLoading[request.id]}
                        >
                          {isCommunicationExpanded
                            ? uiText("Hide Status Chat")
                            : uiText("View Status Chat")}
                        </button>
                      )}
                      {showDirectPurchaseSection && (
                        <button
                          className="text-amber-700 underline"
                          onClick={() =>
                            setExpandedDirectCommId(
                              isDirectPurchaseExpanded ? null : request.id,
                            )
                          }
                          disabled={directPurchaseSending[request.id]}
                        >
                          {isDirectPurchaseExpanded
                            ? uiText("Hide Direct Purchase Note")
                            : uiText("Document Direct Purchase")}
                        </button>
                      )}
                      {request.status === "Approved" && (
                        <button
                          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                          onClick={() =>
                            setExpandedAssignId(
                              expandedAssignId === request.id
                                ? null
                                : request.id,
                            )
                          }
                        >
                          {expandedAssignId === request.id
                            ? uiText("Hide")
                            : request.assigned_user_name
                              ? uiText("Reassign")
                              : uiText("Assign")}
                        </button>
                      )}
                    </div>
                  </div>

                  <AllRequestGuidance
                    request={request}
                    step={uiText(step)}
                    assignedDisplay={
                      assignedDisplay === "Not Assigned" ? undefined : assignedDisplay
                    }
                  />

                  {expandedAssignId === request.id && (
                    <AssignRequestPanel
                      requestId={request.id}
                      currentAssignee={request.assigned_user_name}
                      currentAssigneeId={request.assigned_to ?? null}
                      onSuccess={fetchRequests}
                    />
                  )}

                  {expandedAttachmentsId === request.id && (
                    <div className="mt-4 border-t pt-2">
                      <RequestAttachmentsSection
                        attachments={attachmentsMap[request.id] || []}
                        isLoading={Boolean(attachmentLoadingMap[request.id])}
                        error={attachmentErrorMap[request.id]}
                        onDownload={handleDownloadAttachment}
                        downloadingAttachmentId={downloadingAttachmentId}
                        onRetry={() =>
                          loadAttachmentsForRequest(request.id, { force: true })
                        }
                      />
                    </div>
                  )}

                  {expandedItemsId === request.id && (
                    <section className="mt-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <h3 className="font-semibold text-slate-800">{uiText("Requested Items")}</h3>
                          <p className="text-xs text-gray-500">{uiText("Sort items in this request view only; the saved request order is unchanged.")}</p>
                        </div>
                        {itemsMap[request.id]?.length > 1 && (
                          <button
                            type="button"
                            aria-pressed={alphabetizedItemsId === request.id}
                            onClick={() =>
                              setAlphabetizedItemsId((prev) =>
                                prev === request.id ? null : request.id,
                              )
                            }
                            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm transition hover:bg-gray-100"
                          >
                            {alphabetizedItemsId === request.id
                              ? uiText("Original order")
                              : uiText("Sort A-Z")}
                          </button>
                        )}
                      </div>
                      {loadingItemsId === request.id ? (
                        <p className="text-gray-500">{uiText("Loading items...")}</p>
                      ) : itemsMap[request.id]?.length > 0 ? (
                        <WorkspaceTableScroll><table className="w-full text-sm border">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="border p-1">{uiText("Item")}</th>
                              <th className="border p-1">{uiText("Specs")}</th>
                              <th className="border p-1">{uiText("Brand")}</th>
                              <th className="border p-1">{uiText("Qty")}</th>
                              <th className="border p-1">{uiText("Procured Qty")}</th>
                              <th className="border p-1">{uiText("Unit Cost")}</th>
                              <th className="border p-1">{uiText("Total")}</th>
                              <th className="border p-1">{uiText("Status")}</th>
                              <th className="border p-1">{uiText("Assigned To")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getDisplayItems(
                              itemsMap[request.id],
                              alphabetizedItemsId === request.id,
                            ).map((item, idx) => (
                              <tr key={item.id ?? idx}>
                                <td className="border p-1 align-top font-medium text-gray-900">
                                  {item.item_name}
                                </td>
                                <td className="border p-1 align-top whitespace-pre-wrap text-gray-700">
                                  {formatOptionalItemText(item.specs)}
                                </td>
                                <td className="border p-1 align-top">
                                  {formatOptionalItemText(item.brand)}
                                </td>
                                <td className="border p-1 align-top">
                                  {item.quantity} {item.unit_of_measure || ""}
                                </td>
                                <td className="border p-1 align-top">
                                  {item.purchased_quantity ?? 0}{" "}
                                  {item.unit_of_measure || ""}
                                </td>
                                <td className="border p-1 align-top">
                                  {item.unit_cost}
                                </td>
                                <td className="border p-1 align-top">
                                  {item.total_cost}
                                </td>
                                <td className="border p-1 align-top">
                                  <span
                                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${getItemStatus(item).className}`}
                                  >
                                    {uiText(getItemStatus(item).label)}
                                  </span>
                                </td>
                                <td className="border p-1 align-top">
                                  {item.assigned_user_name || "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table></WorkspaceTableScroll>
                      ) : (
                        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">{uiText("No items found.")}</div>
                      )}
                    </section>
                  )}

                  {expandedApprovalsId === request.id && (
                    <div className="mt-4 border-t pt-2">
                      <ApprovalTimeline
                        approvals={approvalsMap[request.id]}
                        isLoading={loadingApprovalsId === request.id}
                        isUrgent={Boolean(request?.is_urgent)}
                      />
                    </div>
                  )}

                  {showDirectPurchaseSection && isDirectPurchaseExpanded && (
                    <div className="mt-4 border-t pt-3 space-y-3 rounded border-amber-200 bg-amber-50 px-3 py-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-amber-900">{uiText("Urgent Direct Purchase Communication")}</p>
                          <p className="text-xs text-amber-700">{uiText("Document urgent department-led purchasing so supply chain can align and follow up.")}</p>
                        </div>
                        <p className="text-xs text-amber-800 font-medium">{uiText("Recipients: Supply Chain + Requesting Department")}</p>
                      </div>

                      {directPurchaseError[request.id] && (
                        <p className="text-xs text-rose-700">
                          {directPurchaseError[request.id]}
                        </p>
                      )}
                      {directPurchaseSuccess[request.id] && (
                        <p className="text-xs text-emerald-700">
                          {directPurchaseSuccess[request.id]}
                        </p>
                      )}

                      <div className="grid gap-3 md:grid-cols-2">
                        <div className="flex flex-col gap-2">
                          <label className="text-xs font-semibold text-amber-900">{uiText("What is being purchased directly?")}</label>
                          <textarea
                            className="w-full rounded border border-amber-200 bg-white p-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
                            rows={3}
                            placeholder={uiText("Describe the items or services procured directly and who initiated it.")}
                            value={directPurchaseDrafts[request.id] || ""}
                            onChange={(event) =>
                              setDirectPurchaseDrafts((prev) => ({
                                ...prev,
                                [request.id]: event.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="text-xs font-semibold text-amber-900">{uiText("Urgency / policy alignment notes (optional)")}</label>
                          <textarea
                            className="w-full rounded border border-amber-200 bg-white p-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
                            rows={3}
                            placeholder={uiText("Share the urgency reason, risk, or policy guidance needed from supply chain.")}
                            value={directPurchaseUrgency[request.id] || ""}
                            onChange={(event) =>
                              setDirectPurchaseUrgency((prev) => ({
                                ...prev,
                                [request.id]: event.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <button
                          className="bg-amber-600 text-white px-4 py-2 rounded hover:bg-amber-700 disabled:opacity-70"
                          onClick={() =>
                            handleSendDirectCommunication(request.id)
                          }
                          disabled={!!directPurchaseSending[request.id]}
                        >
                          {directPurchaseSending[request.id]
                            ? uiText("Sending...")
                            : uiText("Send update")}
                        </button>
                        <p className="text-xs text-amber-800">{uiText("These notes are logged to the request and shared with supply chain stakeholders.")}</p>
                      </div>

                      {(directPurchaseEntries[request.id] || [])
                        .slice(0, 3)
                        .map((entry) => (
                          <div
                            key={entry.id}
                            className="rounded border border-amber-100 bg-white px-3 py-2 text-xs text-slate-700"
                          >
                            <div className="flex flex-wrap justify-between gap-1">
                              <span className="font-semibold text-amber-900">{uiText("Direct purchase documented")}</span>
                              <span className="text-slate-500">
                                {entry.timestamp
                                  ? new Date(entry.timestamp).toLocaleString()
                                  : ""}
                              </span>
                            </div>
                            <p className="mt-1 whitespace-pre-wrap text-slate-700">
                              {entry.comments}
                            </p>
                          </div>
                        ))}
                    </div>
                  )}

                  {showCommunication && isCommunicationExpanded && (
                    <div className="mt-4 border-t pt-3 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-indigo-900">{uiText("SCM Status Communication")}</p>
                          <p className="text-xs text-indigo-700">{uiText("Discuss the status of this approved request (current status:")}{" "}{statusLabel}).
                          </p>
                        </div>
                        <button
                          type="button"
                          className="text-xs font-medium text-indigo-700 underline"
                          onClick={() => refreshCommunications(request.id)}
                          disabled={communicationLoading[request.id]}
                        >{uiText("Refresh")}</button>
                      </div>

                      <div className="space-y-2">
                        {communicationLoading[request.id] && (
                          <p className="text-xs text-indigo-700">{uiText("Loading communications...")}</p>
                        )}
                        {communicationError[request.id] && (
                          <p className="text-xs text-rose-600">
                            {communicationError[request.id]}
                          </p>
                        )}
                        {communicationSuccess[request.id] && (
                          <p className="text-xs text-emerald-700">
                            {communicationSuccess[request.id]}
                          </p>
                        )}

                        {(communicationList[request.id] || [])
                          .slice(0, 6)
                          .map((entry) => (
                            <div
                              key={entry.id}
                              className="rounded border border-indigo-100 bg-white px-3 py-2 text-xs text-slate-700"
                            >
                              <div className="flex flex-wrap justify-between gap-1">
                                <span className="font-semibold text-indigo-900">
                                  {entry.actor_name || uiText("Unknown")}
                                </span>
                                <span className="text-slate-500">
                                  {entry.status_at_time || uiText("Pending")}
                                </span>
                                <span className="text-slate-400">
                                  {entry.timestamp
                                    ? new Date(entry.timestamp).toLocaleString()
                                    : ""}
                                </span>
                              </div>
                              <p className="mt-1 whitespace-pre-wrap text-slate-700">
                                {entry.comments}
                              </p>
                            </div>
                          ))}

                        {canSendCommunication && (
                          <div className="space-y-2">
                            <textarea
                              className="w-full rounded border border-indigo-200 bg-white p-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                              rows={3}
                              placeholder={uiText("Share an update with the SCM team...")}
                              value={communicationDrafts[request.id] || ""}
                              onChange={(event) =>
                                setCommunicationDrafts((prev) => ({
                                  ...prev,
                                  [request.id]: event.target.value,
                                }))
                              }
                            />
                            <button
                              className="bg-indigo-600 text-white px-3 py-2 rounded hover:bg-indigo-700 disabled:opacity-70"
                              onClick={() =>
                                handleSendCommunication(request.id, statusLabel)
                              }
                              disabled={!!communicationSending[request.id]}
                            >
                              {communicationSending[request.id]
                                ? uiText("Sending...")
                                : uiText("Send to SCM")}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        <PaginationControls
          disabled={loading}
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          className="mt-6"
          summary={`Page ${page} of ${totalPages}`}
        />
      </div>
    </div>
  );
};

export default AllRequestsPage;